/**
 * Cookie utility functions for getting, setting, and deleting browser cookies.
 */

var _COOKIE_JS_ = true;

// Type helper for globalThis access
const _global = globalThis as Record<string, unknown>;

// Initialize COOKIE_MAP on globalThis
if (typeof _global.COOKIE_MAP === 'undefined') {
  _global.COOKIE_MAP = null;
}

// Module-level reference to COOKIE_MAP
let COOKIE_MAP: Record<string, string> | null = _global.COOKIE_MAP as Record<string, string> | null;

function cookiesEnabled(nonSession?: boolean): boolean {
  setCookie(
    "testcookie",
    "testvalue",
    nonSession ? new Date(new Date().getTime() + 60) : undefined
  );
  if (getCookie("testcookie") == null) return false;
  deleteCookie("testcookie");
  return true;
}

function getCookieNames(): Record<string, string> {
  const out: Record<string, string> = {};
  const c = parseCookies();
  for (const i in c) out[i] = i;
  return out;
}

function getCookie(name: string): string | undefined {
  return parseCookies()[name];
}

function setCookie(
  name: string,
  value: string,
  expires?: Date | null,
  path?: string,
  domain?: string,
  secure?: boolean
): void {
  COOKIE_MAP = null;
  _global.COOKIE_MAP = null;
  path = path || "/";
  domain = domain || getCurrentDomain();
  document.cookie =
    escape(name) +
    "=" +
    escape(value) +
    (expires ? ";expires=" + fixCookieDate(expires).toUTCString() : "") +
    (path ? ";path=" + path : "") +
    (domain ? ";domain=" + domain : "") +
    (secure ? ";secure" : "");
}

function deleteCookie(
  name: string,
  path?: string,
  domain?: string,
  secure?: boolean
): void {
  if (getCookieNames()[name]) {
    setCookie(
      name,
      getCookie(name) || "",
      (document as { all?: unknown }).all ? new Date(0) : new Date(),
      path,
      domain,
      secure
    );
    if (getCookie(name) != null && domain == null) {
      deleteCookie(name, undefined, getTopDomain());
    }
  }
}

function parseCookies(): Record<string, string> {
  // Read from globalThis to allow external reset (for tests)
  const currentMap = _global.COOKIE_MAP as Record<string, string> | null;
  if (currentMap != null) return currentMap;
  const out: Record<string, string> = {};
  const c = document.cookie;
  if (c != null && c != "") {
    const clen = c.length;
    let i = 0;
    do {
      let end = c.indexOf("; ", i);
      if (end == -1) end = clen;
      let eq = c.indexOf("=", i);
      if (eq == -1 || eq > end) eq = end;
      out[unescape(c.substring(i, eq))] =
        eq == end ? "" : unescape(c.substring(eq + 1, end));
      i = end + 2;
    } while (i < clen);
  }
  COOKIE_MAP = out;
  _global.COOKIE_MAP = out;
  return out;
}

function getCurrentDomain(): string {
  const l = window.location.href;
  const start = l.indexOf("//") + 2;
  let endColon = l.indexOf(":", start);
  let endSlash = l.indexOf("/", start);
  if (endColon == -1) endColon = l.length;
  if (endSlash == -1) endSlash = l.length;
  return l.substring(start, endColon < endSlash ? endColon : endSlash);
}

function getTopDomain(): string {
  const d = getCurrentDomain();
  return d.substring(d.lastIndexOf(".", d.lastIndexOf(".") - 1));
}

function fixCookieDate(date: Date): Date {
  const base = new Date(0);
  const skew = base.getTime(); // dawn of (Unix) time - should be 0
  if (skew > 0)
    // Except on the Mac - ahead of its time
    date.setTime(date.getTime() - skew);
  return date;
}

if (typeof _TIMEZONETOOLS_JS_ !== "undefined" && _TIMEZONETOOLS_JS_ == true) {
  setTimeZoneCookie();
}

if (typeof _SR_ !== "undefined" && _SR_ != null) {
  _SR_.notify("cookie.js");
}

export {
  cookiesEnabled,
  getCookieNames,
  getCookie,
  setCookie,
  deleteCookie,
  parseCookies,
  getCurrentDomain,
  getTopDomain,
  fixCookieDate,
};

// Expose functions globally for backwards compatibility
_global._COOKIE_JS_ = _COOKIE_JS_;
_global.cookiesEnabled = cookiesEnabled;
_global.getCookieNames = getCookieNames;
_global.getCookie = getCookie;
_global.setCookie = setCookie;
_global.deleteCookie = deleteCookie;
_global.parseCookies = parseCookies;
_global.getCurrentDomain = getCurrentDomain;
_global.getTopDomain = getTopDomain;
_global.fixCookieDate = fixCookieDate;
